var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Stack, Tooltip, Typography, styled, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LocationType } from '../../../enum';
import { RumEntityLabel, rumEntityName } from '../../../observability/rumEntity';
var StyledLocationButton = styled(Button, {
    shouldForwardProp: function (prop) { return prop !== 'isSelected'; },
})(function (_a) {
    var theme = _a.theme, isSelected = _a.isSelected;
    return __assign({ justifyContent: 'flex-start !important', gap: theme.spacing(3) }, (isSelected && {
        color: "".concat(theme.palette.text.primary, " !important"),
        backgroundColor: 'transparent',
        borderColor: "".concat(theme.palette.primary.main, " !important"),
        '&:hover': {
            backgroundColor: 'transparent',
            borderColor: theme.palette.primary.main,
            color: theme.palette.text.primary,
            boxShadow: 'none',
        },
    }));
});
var StyledStack = styled(Stack)(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            maxHeight: theme.typography.pxToRem(400)
        },
        _b["& button"] = {
            marginBottom: theme.typography.pxToRem(8),
        },
        _b["& button:last-child"] = {
            marginBottom: theme.typography.pxToRem(20),
        },
        _b);
});
var LocationListItem = function (_a) {
    var location = _a.location, isSelected = _a.isSelected, status = _a.status, onClick = _a.onClick;
    var t = useTranslation('orgConfig', { keyPrefix: 'hours' }).t;
    var theme = useTheme();
    var trailingIcon = null;
    if (status === 'error') {
        trailingIcon = (_jsx(Tooltip, __assign({ title: t('locations.incompleteConfig') }, { children: _jsx(Typography, __assign({ ml: "auto", color: theme.palette.warning.main }, { children: _jsx(FontAwesomeIcon, { icon: "triangle-exclamation" }) })) })));
    }
    else if (status === 'incomplete') {
        trailingIcon = (_jsx(Typography, __assign({ ml: "auto", color: theme.palette.warning.dark }, { children: _jsx(FontAwesomeIcon, { icon: ['far', 'magnifying-glass-minus'], style: { fontSize: 18 } }) })));
    }
    return (_jsxs(StyledLocationButton, __assign({ variant: "outlined", fullWidth: true, onClick: onClick, isSelected: isSelected, "data-dd-action-name": "Select location: ".concat(rumEntityName('location', location.id)) }, { children: [_jsx(Typography, __assign({ variant: "h3" }, { children: _jsx(FontAwesomeIcon, { icon: "location-dot" }) })), _jsxs(Box, __assign({ textAlign: "left", flexGrow: 1, minWidth: 0 }, { children: [_jsx(Typography, __assign({ fontWeight: "bold", noWrap: true }, { children: _jsx(RumEntityLabel, __assign({ kind: "location", id: location.id, hasActionName: false }, { children: location.locationName })) })), _jsx(Typography, __assign({ variant: "body2", noWrap: true }, { children: location.countryName }))] })), trailingIcon] })));
};
export var LocationListPanel = function (_a) {
    var allLocations = _a.allLocations, selectedLocationId = _a.selectedLocationId, onSelectLocation = _a.onSelectLocation, getLocationStatus = _a.getLocationStatus, _b = _a.groups, groups = _b === void 0 ? true : _b, title = _a.title, description = _a.description;
    var t = useTranslation('orgConfig', { keyPrefix: 'hours' }).t;
    var panelTitle = title !== null && title !== void 0 ? title : t('locations.title');
    var panelDescription = description !== null && description !== void 0 ? description : t('locations.description');
    var renderItem = function (loc) { return (_jsx(LocationListItem, { location: loc, isSelected: loc.id === selectedLocationId, status: getLocationStatus(loc.id), onClick: function () { return onSelectLocation(loc.id); } }, loc.id)); };
    if (!groups) {
        return (_jsxs(_Fragment, { children: [_jsx(Typography, __assign({ variant: "h3", gutterBottom: true }, { children: panelTitle })), _jsx(Typography, __assign({ mb: 5 }, { children: panelDescription })), _jsx(StyledStack, { children: allLocations.map(renderItem) })] }));
    }
    var inHouseLocations = allLocations.filter(function (l) { return l.locationType === LocationType.InHouse; });
    var outsourcedLocations = allLocations.filter(function (l) { return l.locationType === LocationType.Outsourced; });
    return (_jsxs(_Fragment, { children: [_jsx(Typography, __assign({ variant: "h3", gutterBottom: true }, { children: panelTitle })), _jsx(Typography, __assign({ mb: 5 }, { children: panelDescription })), _jsxs(StyledStack, { children: [inHouseLocations.length > 0 && (_jsxs(Stack, __assign({ direction: "column", spacing: 2 }, { children: [_jsx(Typography, __assign({ variant: "subtitle2", fontWeight: "bold" }, { children: t('locations.inHouse') })), inHouseLocations.map(renderItem)] }))), outsourcedLocations.length > 0 && (_jsxs(Stack, __assign({ direction: "column", spacing: 2 }, { children: [_jsx(Typography, __assign({ variant: "subtitle2", fontWeight: "bold" }, { children: t('locations.outsourced') })), outsourcedLocations.map(renderItem)] })))] })] }));
};
